import React, { useEffect, useRef, useState } from 'react';
import styles from './StripeTestCheckout.module.scss';

const AddressAutocomplete = ({
  address1,
  setAddress1,
  address2,
  setAddress2,
  city,
  setCity,
  state,
  setState,
  zip,
  setZip,
  label = 'Address',
  disabled = false
}) => {
  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);
  const [autocompleteEnabled, setAutocompleteEnabled] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [authFailed, setAuthFailed] = useState(false);
  const initAttempted = useRef(false);

  useEffect(() => {
    if (disabled || initAttempted.current) return;

    const handleAuthFailure = () => {
      setShowHint(true);
      setAutocompleteEnabled(false);
      // Force React to replace the DOM node so Google's mutations are gone
      setAuthFailed(true);
    };

    // Register gm_authFailure — Google calls this when the API key is invalid
    const prevAuthFailure = window.gm_authFailure;
    window.gm_authFailure = () => {
      handleAuthFailure();
      if (typeof prevAuthFailure === 'function') prevAuthFailure();
    };

    if (!window.google?.maps?.places?.Autocomplete) {
      setShowHint(true);
      initAttempted.current = true;
      return;
    }

    const timeoutId = setTimeout(() => {
      if (document.querySelector('.dismissible-error')) {
        handleAuthFailure();
        initAttempted.current = true;
        return;
      }

      try {
        initAttempted.current = true;

        autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
          types: ['address'],
          componentRestrictions: { country: 'us' }
        });

        setAutocompleteEnabled(true);

        autocompleteRef.current.addListener('place_changed', () => {
          try {
            const place = autocompleteRef.current.getPlace();
            if (!place?.address_components) return;

            let streetNumber = '';
            let route = '';
            let locality = '';
            let administrativeArea = '';
            let postalCode = '';

            place.address_components.forEach((component) => {
              const types = component.types;
              if (types.includes('street_number')) streetNumber = component.long_name;
              if (types.includes('route')) route = component.long_name;
              if (types.includes('locality')) locality = component.long_name;
              if (types.includes('administrative_area_level_1')) administrativeArea = component.short_name;
              if (types.includes('postal_code')) postalCode = component.long_name;
            });

            setAddress1(`${streetNumber} ${route}`.trim());
            setCity(locality);
            setState(administrativeArea);
            setZip(postalCode);
          } catch (error) {
            console.error('Error processing place:', error);
          }
        });
      } catch (error) {
        console.error('Error initializing autocomplete:', error);
        handleAuthFailure();
      }
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [disabled, setAddress1, setCity, setState, setZip]);

  return (
    <>
      {showHint && !disabled && (
        <p className={styles.hint}>
          Address autocomplete unavailable. Please enter address manually.
        </p>
      )}
      <div className={styles.inputGroup}>
        <label htmlFor={`${label}_address1`} className={styles.label}>
          {label} Line 1
        </label>
        <input
          key={authFailed ? 'plain' : 'autocomplete'}
          ref={authFailed ? null : inputRef}
          id={`${label}_address1`}
          type="text"
          value={address1}
          onChange={(e) => setAddress1(e.target.value)}
          className={styles.input}
          placeholder={autocompleteEnabled ? 'Start typing address...' : '123 Main St'}
          required
          disabled={disabled}
        />
      </div>

      <div className={styles.inputGroup}>
        <label htmlFor={`${label}_address2`} className={styles.label}>
          {label} Line 2 (Optional)
        </label>
        <input
          id={`${label}_address2`}
          type="text"
          value={address2}
          onChange={(e) => setAddress2(e.target.value)}
          className={styles.input}
          placeholder="Apt, Suite, etc."
          disabled={disabled}
        />
      </div>

      <div className={styles.inputGroup}>
        <label htmlFor={`${label}_city`} className={styles.label}>
          City
        </label>
        <input
          id={`${label}_city`}
          type="text"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          className={styles.input}
          placeholder="City"
          required
          disabled={disabled}
        />
      </div>

      <div className={styles.inputGroup}>
        <label htmlFor={`${label}_state`} className={styles.label}>
          State
        </label>
        <input
          id={`${label}_state`}
          type="text"
          value={state}
          onChange={(e) => setState(e.target.value)}
          className={styles.input}
          placeholder="CA"
          required
          disabled={disabled}
        />
      </div>

      <div className={styles.inputGroup}>
        <label htmlFor={`${label}_zip`} className={styles.label}>
          ZIP Code
        </label>
        <input
          id={`${label}_zip`}
          type="text"
          value={zip}
          onChange={(e) => setZip(e.target.value)}
          className={styles.input}
          placeholder="94102"
          required
          disabled={disabled}
        />
      </div>
    </>
  );
};

export default AddressAutocomplete;
